/**
 * Idle session timeout hook.
 *
 * Listens to user activity (mouse, keyboard, scroll, touch) and triggers
 * `onTimeout` once `timeoutMs` of inactivity has passed.
 *
 * Activity tracking is shared across tabs via localStorage so opening
 * a second tab doesn't accidentally extend the original session.
 */
import { useEffect, useRef } from 'react';

const ACTIVITY_KEY = 'lastActivityAt';

export function useIdleTimeout({ enabled, timeoutMs, onTimeout }) {
  const timerRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;

    const markActivity = () => {
      const now = Date.now();
      try {
        localStorage.setItem(ACTIVITY_KEY, String(now));
      } catch (e) {
        // ignore quota errors
      }
      scheduleTimer();
    };

    const scheduleTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      const last = parseInt(localStorage.getItem(ACTIVITY_KEY) || '0', 10);
      const elapsed = Date.now() - last;
      const remaining = Math.max(timeoutMs - elapsed, 0);
      if (remaining === 0) {
        onTimeout();
        return;
      }
      timerRef.current = setTimeout(() => {
        // Re-check on tick (could have been extended by another tab)
        const last2 = parseInt(localStorage.getItem(ACTIVITY_KEY) || '0', 10);
        if (Date.now() - last2 >= timeoutMs) {
          onTimeout();
        } else {
          scheduleTimer();
        }
      }, remaining);
    };

    // Initial activity mark when session starts
    markActivity();

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel'];
    const throttledMark = throttle(markActivity, 500);
    events.forEach((ev) => window.addEventListener(ev, throttledMark, { passive: true }));

    // Cross-tab sync
    const onStorage = (e) => {
      if (e.key === ACTIVITY_KEY) scheduleTimer();
    };
    window.addEventListener('storage', onStorage);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      events.forEach((ev) => window.removeEventListener(ev, throttledMark));
      window.removeEventListener('storage', onStorage);
    };
  }, [enabled, timeoutMs, onTimeout]);
}

function throttle(fn, ms) {
  let lastCall = 0;
  return (...args) => {
    const now = Date.now();
    if (now - lastCall >= ms) {
      lastCall = now;
      fn(...args);
    }
  };
}

export default useIdleTimeout;
